import { AlertType } from "@/types/enums/alert-type";
import { NotificationMethod } from "@/types/enums/notification-method";

/**
 * Alert types that support only a subset of delivery methods; anything absent
 * here supports all of them. Whisper lives in the console — an email or a text
 * for every internal chat message is noise nobody wants, so it is IN_APP only.
 *
 * IMPORTANT: keep in sync with ALERT_TYPE_ALLOWED_METHODS in
 * decoda/enums/alert_type.py — this map hides the toggle, that one refuses the
 * write, and drift means a toggle that 400s.
 */
export const ALERT_TYPE_ALLOWED_METHODS: Partial<
  Record<AlertType, NotificationMethod[]>
> = {
  [AlertType.TEAM_MESSAGE_RECEIVED]: [NotificationMethod.IN_APP],
  // Raised at HQ, which can't hold a texting number.
  [AlertType.COUPA_DRIFT]: [
    NotificationMethod.IN_APP,
    NotificationMethod.EMAIL,
  ],
};

export function methodAllowedForAlertType(
  alertType: AlertType,
  method: NotificationMethod,
): boolean {
  const allowed = ALERT_TYPE_ALLOWED_METHODS[alertType];
  return !allowed || allowed.includes(method);
}

/**
 * Alert types that must not be offered as auto-task rules. A task stores the
 * alert content as its description and subscribes the configured
 * assignee/team/everyone, none of which is intersected with the alert's own
 * audience — so a team message would put a private conversation preview in
 * front of non-members.
 *
 * IMPORTANT: keep in sync with TASK_INELIGIBLE_ALERT_TYPES in
 * decoda/enums/alert_type.py, which refuses to create the task.
 */
export const TASK_INELIGIBLE_ALERT_TYPES: ReadonlySet<AlertType> = new Set([
  AlertType.TEAM_MESSAGE_RECEIVED,
  // Addressed to the one person who asked for the change.
  AlertType.APPROVAL_REQUEST_REVIEWED,
]);

/**
 * Alert types whose in-app toast waits to be dismissed unless the provider turns
 * it off. Everything else clears on its own, as it always has.
 *
 * IMPORTANT: keep in sync with DEFAULT_PERSISTENT_ALERT_TYPES in
 * decoda/enums/alert_type.py, which applies the same default on write.
 */
export const DEFAULT_PERSISTENT_ALERT_TYPES: ReadonlySet<AlertType> = new Set([
  AlertType.TEAM_MESSAGE_RECEIVED,
]);

export function defaultPersistentForAlertType(alertType: AlertType): boolean {
  return DEFAULT_PERSISTENT_ALERT_TYPES.has(alertType);
}

/**
 * Alert types that belong to a conversation. They stack per conversation
 * rather than per type, so their stacks read as messages.
 *
 * IMPORTANT: keep in sync with CONVERSATION_ALERT_TYPES in
 * decoda/enums/alert_type.py, which picks the stack.
 */
export const CONVERSATION_ALERT_TYPES: ReadonlySet<AlertType> = new Set([
  AlertType.MESSAGE_RECEIVED,
  AlertType.COMMUNICATION_MESSAGE_RECEIVED,
  AlertType.TEAM_MESSAGE_RECEIVED,
]);
